import { Injectable } from '@angular/core';
import { FileStorage, StorageBucket } from '@freelancer/file-storage';
import type { ResponseData } from '@freelancer/freelancer-http';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import type {
  CanvasFetchFileParams,
  CanvasFetchFileResponse,
  CanvasUploadFileParams,
  CanvasUploadFileResponse,
} from './canvas.interface';

@UntilDestroy({ className: 'CanvasService' })
@Injectable({
  providedIn: 'root',
})
export class CanvasService {
  constructor(
    private freelancerHttp: FreelancerHttp,
    private fileStorage: FileStorage,
  ) {}

  async uploadFile({
    file,
    contextId,
    contextType,
  }: CanvasUploadFileParams): Promise<CanvasUploadFileResponse> {
    const uploadTask = this.fileStorage.upload(
      StorageBucket.CANVAS_FILES,
      file,
    );
    const completed = await firstValueFrom(
      uploadTask.completes().pipe(untilDestroyed(this)),
    );
    if (completed.state !== 'success') {
      throw new Error('Failed to upload the file.');
    }
    const fileUuid = completed.metadata.id;
    const request$ = this.freelancerHttp.post<{
      url: string;
    }>(
      'canvas/0.1/canvas/attachment',
      {
        file_id: fileUuid,
        context_type: contextType,
        context_id: contextId,
        product_token: 'canvas_files',
      },
      {
        isGaf: false,
      },
    );
    const response = await firstValueFrom(request$.pipe(untilDestroyed(this)));
    if (response.status !== 'success') {
      throw new Error('Failed to persist the file.');
    }
    return {
      url: response.result.url,
      fileUuid,
    };
  }

  async fetchFile({
    fileUuid,
    contextId,
    contextType,
  }: CanvasFetchFileParams): Promise<
    ResponseData<CanvasFetchFileResponse, any>
  > {
    const uri = `canvas/0.1/canvas/attachment/${fileUuid}?context_type=${contextType}&context_id=${contextId}&file_id=${fileUuid}&product_token=canvas_files`;
    const request$ = this.freelancerHttp.get<{ url: string }>(uri, {
      isGaf: false,
    });
    const response = await firstValueFrom(request$.pipe(untilDestroyed(this)));
    return response;
  }

  async fetchAccessToken({
    contextId,
    contextType,
  }: {
    contextId: number;
    contextType: string;
  }): Promise<string> {
    const uri = `canvas/0.1/canvas/?context_type=${contextType}&context_id=${contextId}`;
    const request$ = this.freelancerHttp.get<{
      access_token: string;
    }>(uri, {
      isGaf: false,
    });
    const response = await firstValueFrom(request$.pipe(untilDestroyed(this)));
    if (response.status !== 'success') {
      throw new Error('Failed to fetch the access token');
    }
    return response?.result?.access_token;
  }
}
